import React, { useState, useEffect, useRef } from 'react';
import './modern-table.css';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    CircularProgress
} from '@mui/material';
import { BASE_URL } from "../constants";
import { Eye, Trash2, FileText, Download, CheckCircle } from 'lucide-react';
import { useSnackbar } from '../context/SnackbarContext';
import axios from 'axios';
import { getData, deleteData, postData } from "../api/axiosConfig";
import moment from 'moment';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export const FilesTable = () => {
    const { showSnackbar } = useSnackbar();
    const location = useLocation();
    const { user } = useAuth();
    const userEmail = user?.email || 'anonymous@user.local';
    const [openAlertDialog, setOpenAlertDialog] = useState(false);
    const [fileToDelete, setFileToDelete] = useState({ id: null, name: '' });
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isFromUpload, setIsFromUpload] = useState(false);
    const refreshTimerRef = useRef(null);
    const [pdfPreview, setPdfPreview] = useState({ open: false, url: '', taskId: '' });

    // Multi-select state
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
    const [bulkDeleting, setBulkDeleting] = useState(false);

    useEffect(() => {
        // Check if coming from upload page
        if (location.state?.fromUpload) {
            setIsFromUpload(true);
            // Clear the state after a few seconds
            const timer = setTimeout(() => {
                setIsFromUpload(false);
            }, 15000);
            return () => clearTimeout(timer);
        }
    }, [location.state]);

    useEffect(() => {
        // Fetch files on component mount
        getFiles();
        startRefreshTimer();

        return () => {
            if (refreshTimerRef.current) {
                clearInterval(refreshTimerRef.current);
            }
        };
    }, []);

    const startRefreshTimer = () => {
        refreshTimerRef.current = setInterval(async () => {
            await getFiles(true); // Pass true for background refresh
        }, 30000); // Refresh every 30 seconds
    };

    const getFiles = async (isBackgroundRefresh = false) => {
        try {
            if (!isBackgroundRefresh) {
                setLoading(true);
            }

            // Fetch all documents (no user filter since auth is removed)
            const data = await getData(`/api/documents?user_id=${encodeURIComponent(userEmail)}`);
            // Handle both {documents: [...]} and [...] response formats
            const documentsArray = data?.documents || data || [];
            if (JSON.stringify(documentsArray) !== JSON.stringify(files)) {
                setFiles(documentsArray);
                // Clear selections that no longer exist
                setSelectedIds(prev => {
                    const validIds = new Set(documentsArray.map(f => f.task_id));
                    const next = new Set([...prev].filter(id => validIds.has(id)));
                    return next.size === prev.size ? prev : next;
                });
            }
        } catch (error) {
            console.error("Error fetching documents:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleViewFile = (taskId, status) => {
        // Route based on status
        if (status === 'completed') {
            window.location.href = `/files/${taskId}/compliance`;
        } else {
            window.location.href = `/files/${taskId}`;
        }
    }

    const handleViewInputDocument = (taskId, status) => {
        // Route based on status
        if (status === 'completed') {
            window.location.href = `/files/${taskId}/compliance`;
        } else {
            window.location.href = `/files/${taskId}`;
        }
    }

    const handleClosePdfPreview = () => {
        setPdfPreview({ open: false, url: '', taskId: '' });
    }

    const handleDownloadFile = async (taskId) => {
        try {
            // Use streaming endpoint directly for download
            const downloadUrl = `${BASE_URL}/api/documents/stream-report?task_id=${taskId}&user_id=${encodeURIComponent(userEmail)}`;

            // Create a temporary link to trigger download
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = 'compliance_report.pdf';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            showSnackbar({
                message: "Download started",
                severity: "success"
            });
        } catch (error) {
            console.error("Error downloading file:", error);
            showSnackbar({
                message: 'Error downloading file',
                severity: "error"
            })
        }
    }

    const handleDownloadRemediated = async (taskId) => {
        try {
            // Use streaming endpoint directly for download
            const downloadUrl = `${BASE_URL}/api/documents/stream-remediated?task_id=${taskId}&user_id=${encodeURIComponent(userEmail)}`;

            // Create a temporary link to trigger download
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = 'remediated.pdf';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            showSnackbar({
                message: "Downloading remediated PDF...",
                severity: "success"
            });
        } catch (error) {
            console.error("Error downloading remediated PDF:", error);
            showSnackbar({
                message: 'Error downloading remediated PDF',
                severity: "error"
            })
        }
    }

    const handleAlertClose = () => {
        setOpenAlertDialog(false);
        setFileToDelete({ id: null, name: '' });
    };

    const handleDelete = (file) => {
        setFileToDelete({
            id: file.task_id,
            name: file.input_file_name || file.s3_key ? (file.input_file_name || file.s3_key.split('/').pop()) : 'Unknown file'
        });
        setOpenAlertDialog(true);
    }

    const formatDate = (dateString) => {
        if (!dateString) return { date: 'N/A', time: '' };

        // Parse the date and convert to local timezone
        const momentDate = moment.utc(dateString).local();
        const date = momentDate.format('MMM D, YYYY');
        const time = momentDate.format('hh:mm A');

        return { date, time };
    };

    const formatFileName = (s3Key) => {
        if (!s3Key) return 'Unknown';

        // Extract filename from path
        const fullFileName = s3Key.split('/').pop();

        // Remove extension
        const fileNameWithoutExt = fullFileName.substring(0, fullFileName.lastIndexOf('.')) || fullFileName;

        // Trim if exceeds 40 characters
        const maxLength = 40;
        if (fileNameWithoutExt.length > maxLength) {
            return fileNameWithoutExt.substring(0, maxLength) + '...';
        }

        return fileNameWithoutExt;
    };

    const handleConfirmDelete = async (taskId) => {
        try {
            await deleteData(`/api/documents/${taskId}?user_id=${encodeURIComponent(userEmail)}`);

            setOpenAlertDialog(false);
            setFileToDelete({ id: null, name: '' });
            showSnackbar({
                message: "File deleted successfully",
                severity: "success"
            });
            getFiles();
        } catch (error) {
            console.error("Error deleting file:", error);
            showSnackbar({
                message: error.response?.data?.detail || 'Error deleting file',
                severity: "error"
            });
        }
    };

    // ── Multi-select handlers ──────────────────────────────────────
    const toggleSelect = (taskId) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(taskId)) {
                next.delete(taskId);
            } else {
                next.add(taskId);
            }
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (selectedIds.size === files.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(files.map(f => f.task_id)));
        }
    };

    const handleBulkDeleteClick = () => {
        if (selectedIds.size === 0) return;
        setBulkDeleteOpen(true);
    };

    const handleBulkDeleteClose = () => {
        setBulkDeleteOpen(false);
    };

    const handleConfirmBulkDelete = async () => {
        setBulkDeleting(true);
        try {
            const taskIds = [...selectedIds];
            await postData('/api/documents/bulk-delete', { task_ids: taskIds, user_id: userEmail });

            showSnackbar({
                message: `${taskIds.length} file${taskIds.length > 1 ? 's' : ''} deleted successfully`,
                severity: "success"
            });

            setSelectedIds(new Set());
            setBulkDeleteOpen(false);
            getFiles();
        } catch (error) {
            console.error("Bulk delete error:", error);
            showSnackbar({
                message: error?.detail || 'Error deleting files',
                severity: "error"
            });
        } finally {
            setBulkDeleting(false);
        }
    };

    const allSelected = files.length > 0 && selectedIds.size === files.length;
    const someSelected = selectedIds.size > 0 && selectedIds.size < files.length;

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="table-card">
                <div className="table-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                        <h3 className="table-title">Files Management</h3>
                        <p className="table-subtitle">View and manage your uploaded files</p>
                    </div>
                    {selectedIds.size > 0 && (
                        <button
                            onClick={handleBulkDeleteClick}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '8px 16px',
                                borderRadius: '8px',
                                border: '1px solid #fecaca',
                                background: 'linear-gradient(135deg, #fef2f2 0%, #ffffff 100%)',
                                color: '#dc2626',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: 600,
                                transition: 'all 0.2s ease',
                                boxShadow: '0 1px 3px rgba(220, 38, 38, 0.1)',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'linear-gradient(135deg, #fee2e2 0%, #fef2f2 100%)';
                                e.currentTarget.style.boxShadow = '0 4px 8px rgba(220, 38, 38, 0.15)';
                                e.currentTarget.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'linear-gradient(135deg, #fef2f2 0%, #ffffff 100%)';
                                e.currentTarget.style.boxShadow = '0 1px 3px rgba(220, 38, 38, 0.1)';
                                e.currentTarget.style.transform = 'translateY(0)';
                            }}
                        >
                            <Trash2 style={{ width: '14px', height: '14px' }} />
                            Delete Selected ({selectedIds.size})
                        </button>
                    )}
                </div>

                {loading && (
                    <div className='table-loading'>
                        <CircularProgress />
                        <span className="ml-3">{isFromUpload ? 'Processing the document...' : 'Loading files...'}</span>
                    </div>
                )}

                {!loading && files.length === 0 && (
                    <div className='table-empty'>
                        <FileText className="table-empty-icon" />
                        <p>No files available</p>
                        <p className="text-sm">Upload files to get started</p>
                    </div>
                )}

                {!loading && files.length > 0 && (
                    <div className="overflow-x-auto">
                        <table className="modern-table files-management-table" style={{ minWidth: '900px', width: '100%' }}>
                            <thead>
                                <tr>
                                    <th style={{ width: '48px', textAlign: 'center', paddingLeft: '8px', paddingRight: '0' }}>
                                        <input
                                            type="checkbox"
                                            checked={allSelected}
                                            ref={el => { if (el) el.indeterminate = someSelected; }}
                                            onChange={toggleSelectAll}
                                            title="Select all files"
                                            style={{
                                                width: '16px',
                                                height: '16px',
                                                cursor: 'pointer',
                                                accentColor: '#3b82f6',
                                            }}
                                        />
                                    </th>
                                    <th style={{ width: '38%', textAlign: 'left', paddingLeft: '8px' }}>File Name</th>
                                    <th style={{ width: '15%', textAlign: 'center' }}>Status</th>
                                    <th style={{ width: '20%', textAlign: 'center' }}>Uploaded Date</th>
                                    <th style={{ width: '15%', textAlign: 'center' }}>Results</th>
                                    <th style={{ width: '10%', textAlign: 'center' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {files.map((file, index) => {
                                    const isSelected = selectedIds.has(file.task_id);
                                    return (
                                        <tr
                                            key={file.task_id}
                                            style={{
                                                background: isSelected ? '#eff6ff' : undefined,
                                            }}
                                        >
                                            <td style={{ textAlign: 'center', width: '48px', paddingLeft: '8px', paddingRight: '0' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => toggleSelect(file.task_id)}
                                                    title={`Select ${file.input_file_name || 'file'}`}
                                                    style={{
                                                        width: '16px',
                                                        height: '16px',
                                                        cursor: 'pointer',
                                                        accentColor: '#3b82f6',
                                                    }}
                                                />
                                            </td>
                                            <td style={{ textAlign: 'left', paddingLeft: '8px' }}>
                                                <div className="flex items-center gap-3">
                                                    <div className="file-icon">
                                                        <FileText className="w-4 h-4 text-white" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <button
                                                            onClick={() => handleViewInputDocument(file.task_id, file.status)}
                                                            disabled={file.status === 'processing' || file.status === 'failed' || file.status === 'remediating'}
                                                            style={{
                                                                background: 'none',
                                                                border: 'none',
                                                                padding: 0,
                                                                cursor: (file.status === 'processing' || file.status === 'failed' || file.status === 'remediating') ? 'not-allowed' : 'pointer',
                                                                textAlign: 'left',
                                                                width: '100%'
                                                            }}
                                                        >
                                                            <div
                                                                className="font-medium text-gray-900 truncate"
                                                                title={file.input_file_name || (file.s3_key ? file.s3_key.split('/').pop() : 'Unknown')}
                                                                style={{
                                                                    color: (file.status === 'processing' || file.status === 'failed' || file.status === 'remediating') ? '#9ca3af' : '#1f2937',
                                                                    fontSize: '14px',
                                                                    fontWeight: 500,
                                                                    transition: 'all 0.2s ease',
                                                                    opacity: (file.status === 'processing' || file.status === 'failed' || file.status === 'remediating') ? 0.5 : 1
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    if (file.status !== 'processing' && file.status !== 'failed' && file.status !== 'remediating') {
                                                                        e.currentTarget.style.color = '#2563eb';
                                                                    }
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    if (file.status !== 'processing' && file.status !== 'failed' && file.status !== 'remediating') {
                                                                        e.currentTarget.style.color = '#1f2937';
                                                                    }
                                                                }}
                                                            >
                                                                {file.input_file_name ? file.input_file_name.replace(/\.[^/.]+$/, '').substring(0, 40) : formatFileName(file.s3_key)}
                                                            </div>
                                                        </button>
                                                    </div>
                                                </div>
                                            </td>

                                            <td style={{ textAlign: 'center' }}>
                                                <div style={{ display: 'flex', justifyContent: 'center' }}>
                                                    <span
                                                        className={`status-badge ${file.status === 'remediated' ? 'processed' :
                                                            file.status === 'completed' ? 'completed-audit' :
                                                                file.status === 'remediating' ? 'remediating' :
                                                                    'pending'
                                                            }`}
                                                    >
                                                        {file.status === 'completed' ? 'audit complete' :
                                                            file.status === 'remediating' ? 'remediating...' :
                                                                file.status}
                                                    </span>
                                                </div>
                                            </td>

                                            <td style={{ textAlign: 'center' }}>
                                                <div style={{ display: 'inline-block', textAlign: 'left' }}>
                                                    <div className="font-medium">{formatDate(file.created_at).date}</div>
                                                    <div className="text-sm text-gray-500">{formatDate(file.created_at).time}</div>
                                                </div>
                                            </td>

                                            <td>
                                                <div className="action-buttons-wrapper" style={{ justifyContent: 'center' }}>
                                                    <div className="action-buttons">
                                                        <button
                                                            className="action-button view"
                                                            onClick={() => handleViewFile(file.task_id, file.status)}
                                                            title={file.status === 'completed' ? 'View Compliance Report' : 'View File Details'}
                                                            disabled={file.status === 'processing' || file.status === 'failed' || file.status === 'remediating'}
                                                            style={{
                                                                opacity: (file.status === 'processing' || file.status === 'failed' || file.status === 'remediating') ? 0.5 : 1,
                                                                cursor: (file.status === 'processing' || file.status === 'failed' || file.status === 'remediating') ? 'not-allowed' : 'pointer'
                                                            }}
                                                        >
                                                            <Eye className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            className="action-button download"
                                                            onClick={() => handleDownloadFile(file.task_id)}
                                                            title="Download Report"
                                                            disabled={file.status !== 'remediated'}
                                                            style={{
                                                                opacity: file.status !== 'remediated' ? 0.5 : 1,
                                                                cursor: file.status !== 'remediated' ? 'not-allowed' : 'pointer'
                                                            }}
                                                        >
                                                            <Download className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            className="action-button"
                                                            onClick={() => handleDownloadRemediated(file.task_id)}
                                                            title="Download Remediated PDF"
                                                            disabled={file.status !== 'remediated'}
                                                            style={{
                                                                opacity: file.status !== 'remediated' ? 0.5 : 1,
                                                                cursor: file.status !== 'remediated' ? 'not-allowed' : 'pointer',
                                                                background: 'linear-gradient(135deg, #dcfce7 0%, #ffffff 100%)',
                                                                border: '1px solid #86efac'
                                                            }}
                                                        >
                                                            <CheckCircle className="w-4 h-4" style={{ color: '#16a34a' }} />
                                                        </button>
                                                    </div>
                                                </div>
                                            </td>

                                            <td style={{ textAlign: 'center' }}>
                                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                    <button
                                                        onClick={() => handleDelete(file)}
                                                        title="Delete File"
                                                        style={{
                                                            padding: '6px 10px',
                                                            borderRadius: '6px',
                                                            border: '1px solid #fee2e2',
                                                            background: 'linear-gradient(135deg, #fef2f2 0%, #ffffff 100%)',
                                                            color: '#dc2626',
                                                            cursor: 'pointer',
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: '4px',
                                                            fontSize: '12px',
                                                            fontWeight: 500,
                                                            transition: 'all 0.2s ease',
                                                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.background = 'linear-gradient(135deg, #fee2e2 0%, #fef2f2 100%)';
                                                            e.currentTarget.style.borderColor = '#fecaca';
                                                            e.currentTarget.style.transform = 'translateY(-1px)';
                                                            e.currentTarget.style.boxShadow = '0 4px 6px rgba(220, 38, 38, 0.15)';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.background = 'linear-gradient(135deg, #fef2f2 0%, #ffffff 100%)';
                                                            e.currentTarget.style.borderColor = '#fee2e2';
                                                            e.currentTarget.style.transform = 'translateY(0)';
                                                            e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)';
                                                        }}
                                                    >
                                                        <Trash2 style={{ width: '13px', height: '13px' }} />
                                                        <span>Delete</span>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* PDF Preview Dialog */}
            <Dialog
                open={pdfPreview.open}
                onClose={handleClosePdfPreview}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        height: '90vh',
                        borderRadius: '16px',
                        background: '#ffffff',
                        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
                    }
                }}
            >
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    {/* Header */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '16px',
                        borderBottom: '1px solid #e5e7eb'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{
                                width: '40px',
                                height: '40px',
                                background: '#eff6ff',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <FileText className="w-5 h-5 text-blue-500" style={{ color: '#3b82f6' }} />
                            </div>
                            <div>
                                <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#111827', margin: 0 }}>PDF Preview</h3>
                                <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>Task ID: {pdfPreview.taskId}</p>
                            </div>
                        </div>
                        <button
                            onClick={handleClosePdfPreview}
                            style={{
                                padding: '8px',
                                borderRadius: '8px',
                                border: 'none',
                                background: 'transparent',
                                cursor: 'pointer'
                            }}
                            title="Close Preview"
                            onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                            <svg style={{ width: '20px', height: '20px', color: '#6b7280' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* PDF Viewer */}
                    <div style={{ flex: 1, overflow: 'hidden', background: '#f9fafb' }}>
                        {pdfPreview.url ? (
                            <iframe
                                src={pdfPreview.url}
                                style={{ width: '100%', height: '100%', border: 0 }}
                                title="PDF Preview"
                            />
                        ) : (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                <CircularProgress />
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="p-4 border-t border-gray-200 flex justify-end gap-3">
                        <Button
                            onClick={() => handleDownloadFile(pdfPreview.taskId)}
                            variant="outlined"
                            startIcon={<Download className="w-4 h-4" />}
                            sx={{
                                borderRadius: '8px',
                                textTransform: 'none',
                                fontWeight: 500,
                                paddingX: '20px',
                                paddingY: '8px',
                                borderColor: '#d1d5db',
                                color: '#6b7280',
                                '&:hover': {
                                    borderColor: '#9ca3af',
                                    backgroundColor: '#f9fafb'
                                }
                            }}
                        >
                            Download
                        </Button>
                        <Button
                            onClick={handleClosePdfPreview}
                            variant="contained"
                            sx={{
                                borderRadius: '8px',
                                textTransform: 'none',
                                fontWeight: 500,
                                paddingX: '20px',
                                paddingY: '8px',
                                backgroundColor: '#3b82f6',
                                '&:hover': {
                                    backgroundColor: '#2563eb'
                                }
                            }}
                        >
                            Close
                        </Button>
                    </div>
                </div>
            </Dialog>

            {/* Single Delete Confirmation Dialog */}
            <Dialog
                open={openAlertDialog}
                onClose={handleAlertClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        padding: '8px',
                        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.05)',
                        backdropFilter: 'blur(10px)',
                    }
                }}
            >
                <div className="p-6">
                    {/* Icon and Title Section */}
                    <div className="flex items-center gap-4 mb-6">
                        <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center">
                            <Trash2 className="w-6 h-6 text-red-500" />
                        </div>
                        <div>
                            <DialogTitle
                                id="alert-dialog-title"
                                sx={{
                                    padding: 0,
                                    fontSize: '1.25rem',
                                    fontWeight: 600,
                                    color: '#1f2937',
                                    marginBottom: '4px'
                                }}
                            >
                                Delete File
                            </DialogTitle>
                            <p className="text-sm text-gray-500 m-0">This action cannot be undone</p>
                        </div>
                    </div>

                    {/* Content Section */}
                    <DialogContent sx={{ padding: 0, marginBottom: '24px' }}>
                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                            <DialogContentText
                                id="alert-dialog-description"
                                sx={{
                                    color: '#374151',
                                    fontSize: '0.95rem',
                                    lineHeight: 1.6,
                                    margin: 0
                                }}
                            >
                                Are you sure you want to permanently delete this file?
                            </DialogContentText>
                            <div className="mt-3 p-3 bg-white rounded-md border border-gray-200">
                                <div className="flex items-center gap-2">
                                    <FileText className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                    <span className="font-medium text-gray-900 truncate">
                                        {fileToDelete.name}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </DialogContent>

                    {/* Action Buttons */}
                    <DialogActions sx={{ padding: 0, gap: '12px', justifyContent: 'flex-end' }}>
                        <Button
                            onClick={handleAlertClose}
                            variant="outlined"
                            sx={{
                                borderRadius: '8px',
                                textTransform: 'none',
                                fontWeight: 500,
                                paddingX: '20px',
                                paddingY: '8px',
                                borderColor: '#d1d5db',
                                color: '#6b7280',
                                '&:hover': {
                                    borderColor: '#9ca3af',
                                    backgroundColor: '#f9fafb'
                                }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={() => handleConfirmDelete(fileToDelete.id)}
                            variant="contained"
                            sx={{
                                borderRadius: '8px',
                                textTransform: 'none',
                                fontWeight: 500,
                                paddingX: '20px',
                                paddingY: '8px',
                                backgroundColor: '#dc2626',
                                '&:hover': {
                                    backgroundColor: '#b91c1c'
                                },
                                boxShadow: '0 4px 12px rgba(220, 38, 38, 0.2)'
                            }}
                            autoFocus
                        >
                            Delete File
                        </Button>
                    </DialogActions>
                </div>
            </Dialog>

            {/* Bulk Delete Confirmation Dialog */}
            <Dialog
                open={bulkDeleteOpen}
                onClose={handleBulkDeleteClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        padding: '8px',
                        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.05)',
                        backdropFilter: 'blur(10px)',
                    }
                }}
            >
                <div className="p-6">
                    <div className="flex items-center gap-4 mb-6">
                        <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center">
                            <Trash2 className="w-6 h-6 text-red-500" />
                        </div>
                        <div>
                            <DialogTitle
                                sx={{
                                    padding: 0,
                                    fontSize: '1.25rem',
                                    fontWeight: 600,
                                    color: '#1f2937',
                                    marginBottom: '4px'
                                }}
                            >
                                Delete {selectedIds.size} File{selectedIds.size > 1 ? 's' : ''}
                            </DialogTitle>
                            <p className="text-sm text-gray-500 m-0">This action cannot be undone</p>
                        </div>
                    </div>

                    <DialogContent sx={{ padding: 0, marginBottom: '24px' }}>
                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                            <DialogContentText
                                sx={{
                                    color: '#374151',
                                    fontSize: '0.95rem',
                                    lineHeight: 1.6,
                                    margin: 0,
                                    marginBottom: '12px',
                                }}
                            >
                                Are you sure you want to permanently delete {selectedIds.size} selected file{selectedIds.size > 1 ? 's' : ''}?
                            </DialogContentText>
                            <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                                {files
                                    .filter(f => selectedIds.has(f.task_id))
                                    .map(f => (
                                        <div key={f.task_id} className="flex items-center gap-2 p-2 bg-white rounded-md border border-gray-200 mb-2">
                                            <FileText className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                            <span className="text-sm text-gray-900 truncate">
                                                {f.input_file_name || (f.s3_key ? f.s3_key.split('/').pop() : f.task_id)}
                                            </span>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    </DialogContent>

                    <DialogActions sx={{ padding: 0, gap: '12px', justifyContent: 'flex-end' }}>
                        <Button
                            onClick={handleBulkDeleteClose}
                            variant="outlined"
                            disabled={bulkDeleting}
                            sx={{
                                borderRadius: '8px',
                                textTransform: 'none',
                                fontWeight: 500,
                                paddingX: '20px',
                                paddingY: '8px',
                                borderColor: '#d1d5db',
                                color: '#6b7280',
                                '&:hover': {
                                    borderColor: '#9ca3af',
                                    backgroundColor: '#f9fafb'
                                }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleConfirmBulkDelete}
                            variant="contained"
                            disabled={bulkDeleting}
                            sx={{
                                borderRadius: '8px',
                                textTransform: 'none',
                                fontWeight: 500,
                                paddingX: '20px',
                                paddingY: '8px',
                                backgroundColor: '#dc2626',
                                '&:hover': {
                                    backgroundColor: '#b91c1c'
                                },
                                boxShadow: '0 4px 12px rgba(220, 38, 38, 0.2)'
                            }}
                        >
                            {bulkDeleting ? (
                                <CircularProgress size={20} sx={{ color: 'white' }} />
                            ) : (
                                `Delete ${selectedIds.size} File${selectedIds.size > 1 ? 's' : ''}`
                            )}
                        </Button>
                    </DialogActions>
                </div>
            </Dialog>
        </div>
    );
};